import { removeUndefinedFromObject } from '../utils/url'
import moment from 'moment'
import { toISOString } from '../utils/dateUtils'

/**
 * Serialize dates in object to ISO strings for API
 * Recursively converts moment objects to ISO strings
 * @param {*} obj - Object to serialize
 * @returns {*} Serialized object
 */
export const serializeDates = (obj) => {
  if (!obj || typeof obj !== 'object') return obj

  if (moment.isMoment(obj)) {
    return toISOString(obj)
  }

  if (Array.isArray(obj)) {
    return obj.map(serializeDates)
  }

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    if (moment.isMoment(value)) {
      result[key] = toISOString(value)
    } else if (value && typeof value === 'object') {
      result[key] = serializeDates(value)
    } else {
      result[key] = value
    }
  }
  return result
}

export const createURL = (url, query) => {
  if (query) {
    const { page, limit, sort, order, ...rest } = query
    const params = new URLSearchParams(
      removeUndefinedFromObject({
        ...rest,
        _page: page,
        _limit: limit,
        _sort: sort,
        _order: order,
      }),
    )
    return `${url}?${params.toString()}`
  }
  return url
}

export const createCRUDActions = (defaultFetchId, url) => ({
  list: (query, options = {}) => {
    const _fetchId = options.fetchId ? options.fetchId : defaultFetchId
    const { signal, ...restOptions } = options // eslint-disable-line no-unused-vars
    return {
      type: `FETCH_${_fetchId.toUpperCase()}_LIST`,
      meta: { fetch_id: _fetchId },
      $payload: {
        url: createURL(url, query),
        options: signal ? { signal } : undefined,
      },
    }
  },

  get: (id, { resultTransform, fetchId, query } = {}) => ({
    type: `FETCH_${defaultFetchId.toUpperCase()}_SINGLE`,
    meta: {
      fetch_id: fetchId || defaultFetchId,
      type: 'single',
      resultTransform,
    },
    $payload: {
      url: query ? createURL(`${url}/${id}`, query) : `${url}/${id}`,
    },
  }),

  create: (resource, { onResponse, resultTransform, fetchId } = {}) => ({
    type: `CREATE_${defaultFetchId.toUpperCase()}`,
    meta: {
      fetch_id: fetchId || defaultFetchId,
      type: 'create',
      resultTransform,
    },
    $payload: {
      url: url,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(serializeDates(resource)),
      },
      onResponse,
    },
  }),

  update: (resource, { onResponse, resultTransform, fetchId } = {}) => ({
    type: `UPDATE_${defaultFetchId.toUpperCase()}`,
    meta: {
      fetch_id: fetchId || defaultFetchId,
      type: 'update',
      resultTransform,
    },
    $payload: {
      url: `${url}/${resource._id}`,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'PUT',
        body: JSON.stringify(serializeDates(resource)),
      },
      onResponse,
    },
  }),

  delete: (_id, { onResponse, resultTransform, fetchId } = {}) => ({
    type: `DELETE_${defaultFetchId.toUpperCase()}`,
    meta: {
      fetch_id: fetchId || defaultFetchId,
      type: 'delete',
      _id,
      resultTransform,
    },
    $payload: {
      url: `${url}/${_id}`,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'DELETE',
      },
      responseType: 'text',
      onResponse,
    },
  }),

  clear: (type = 'single', options = {}) => ({
    type: 'CLEAR_FETCH_DATA',
    meta: { fetch_id: options.fetchId || defaultFetchId, type },
  }),
})
